<template>
    <div>
        <page-header
            :text="$t('navigation.notification')"
            :searchField="{
                placeholder: $t('notifications.search'),
                inputEmitEvent: 'search',
                allowedFields: searchFields,
                defaultField: 'title',
                savedSearchName: 'search-notification-overview',
            }"
            @search="searchHandler"
        />

        <div class="subheader subheader--small">
            <div class="row">
                <div class="column-tablet-16">
                    <h2 class="subheader__headline">
                        {{ $t('notifications.pending_notifications') }}
                    </h2>
                </div>

                <div class="column-tablet-8 align-right">
                    <a class="button button--large" @click.prevent="createEditNotification(true)">
                        <i class="icon icon--action-add"></i>
                        <span>{{ $t('notifications.create_notification') }}</span>
                    </a>
                </div>
            </div>
        </div>

        <div class="table-wrapper">
            <table class="table table--striped" id="pending_notifications">
                <thead>
                    <tr>
                        <th
                            v-for="(field, i) in pending.fields"
                            :key="'pending_' + i + field.value"
                            class="table-filter__trigger"
                            :width="field.width"
                        >
                            <div>{{ field.title }}</div>
                            <div
                                v-if="field.sortable"
                                class="table-filter__sorting"
                                @click="sortClickHandler('pending', field.value)"
                                :class="{
                                    asc: pending.activeSorting[field.value] === 'asc',
                                    desc: pending.activeSorting[field.value] === 'desc',
                                }"
                            >
                                <i class="icon icon--common-caret"></i>
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="notification in filteredNotificationsPending"
                        :key="notification.id"
                        class="table__row table__row--clickable"
                    >
                        <td>
                            <b>{{ notification.title }}</b>
                        </td>
                        <td>{{ notification.body }}</td>
                        <td>
                            <span v-if="notification.locale">{{
                                $t('language.' + notification.locale)
                            }}</span>
                            <span v-else class="capitalize">{{ $t('common.all') }}</span>
                        </td>
                        <td>
                            <span v-if="notification.via_email">{{ $t('common.yes') }}</span>
                            <span v-else>{{ $t('common.no') }}</span>
                        </td>
                        <td>
                            <span v-if="notification.via_push">{{ $t('common.yes') }}</span>
                            <span v-else>{{ $t('common.no') }}</span>
                        </td>
                        <td>
                            <a
                                @click.prevent="viewSendNotification(notification, false)"
                                href="#"
                                class="button button--primary"
                                >{{ $t('notifications.send') }}</a
                            >
                        </td>
                        <td class="nowrap">
                            <a
                                class="button button--attention"
                                @click="createEditNotification(false, notification)"
                                :class="{ 'ng-hide': notification.loading }"
                                :title="$t('notifications.update_notification')"
                            >
                                <i class="icon icon--2x icon--action-edit-in-circle"></i>
                            </a>

                            <a
                                class="button button--attention"
                                @click.prevent="deleteNotification(notification)"
                                :class="{ 'ng-hide': notification.loading }"
                                :title="$t('notifications.delete_notification')"
                            >
                                <i class="icon icon--2x icon--action-decline"></i>
                            </a>

                            <i
                                class="icon icon--common-spinner spinner"
                                :class="{ 'spinner--finished': !notification.loading }"
                            ></i>
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="pending.paginationData"
                    :paginationData="pending.paginationData"
                    :visibleAmount="
                        activeSearchTerm !== ''
                            ? pending.searchedNotifications.length
                            : pending.notifications.length
                    "
                    @goToPrevPage="pageHandler('pending', -1)"
                    @goToNextPage="pageHandler('pending', 1)"
                />
            </table>
        </div>

        <div
            class="alert alert--danger"
            role="alert"
            v-if="pendingLoaded && filteredNotificationsPending.length === 0"
        >
            {{ $t('notifications.no_pending_notifications') }}
        </div>

        <div class="subheader subheader--small">
            <div class="row">
                <div class="column-tablet-24">
                    <h2 class="subheader__headline">
                        {{ $t('notifications.sent_notifications') }}
                    </h2>
                </div>
            </div>
        </div>

        <div class="table-wrapper">
            <table class="table table--striped" id="sent_notifications">
                <thead>
                    <tr>
                        <th
                            v-for="(field, i) in sent.fields"
                            :key="'sent_' + i + field.value"
                            class="table-filter__trigger"
                            :width="field.width"
                        >
                            <div>{{ field.title }}</div>
                            <div
                                v-if="field.sortable"
                                class="table-filter__sorting"
                                @click="sortClickHandler('sent', field.value)"
                                :class="{
                                    asc: sent.activeSorting[field.value] === 'asc',
                                    desc: sent.activeSorting[field.value] === 'desc',
                                }"
                            >
                                <i class="icon icon--common-caret"></i>
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="notification in filteredNotificationsSent"
                        :key="notification.id"
                        class="table__row table__row--clickable"
                    >
                        <td>
                            <b>{{ notification.title }}</b>
                        </td>
                        <td>{{ notification.body }}</td>
                        <td>
                            <span v-if="notification.locale">{{
                                $t('language.' + notification.locale)
                            }}</span>
                            <span v-else class="capitalize">{{ $t('common.all') }}</span>
                        </td>
                        <td>
                            <span v-if="notification.via_email">{{ $t('common.yes') }}</span>
                            <span v-else>{{ $t('common.no') }}</span>
                        </td>
                        <td>
                            <span v-if="notification.via_push">{{ $t('common.yes') }}</span>
                            <span v-else>{{ $t('common.no') }}</span>
                        </td>
                        <td>{{ notification.completed }} / {{ notification.scheduled }}</td>
                        <td>
                            <span v-if="notification.triggered_at">
                                <span class="nowrap">{{
                                    notification.triggered_at | formatDate
                                }}</span>
                            </span>
                        </td>
                        <td>{{ notification.triggered_by_name }}</td>
                        <td>
                            {{ getOrganizationName(notification.organization_id) }}
                        </td>
                    </tr>
                </tbody>

                <TablePagination
                    v-if="sent.paginationData"
                    :paginationData="sent.paginationData"
                    :visibleAmount="
                        activeSearchTerm !== ''
                            ? sent.searchedNotifications.length
                            : sent.notifications.length
                    "
                    @goToPrevPage="pageHandler('sent', -1)"
                    @goToNextPage="pageHandler('sent', 1)"
                />
            </table>
        </div>

        <div
            class="alert alert--danger"
            role="alert"
            v-if="sentLoaded && filteredNotificationsSent.length === 0"
        >
            {{ $t('notifications.no_sent_notifications') }}
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import PageHeader from '@/components/PageHeader';
import * as moment from 'moment';
import debounce from 'lodash/debounce';
import { createSearchParams } from '@/utils/searchParameters';
import eventBus from '@/utils/eventBus';
import TablePagination from '@/components/TablePagination';
import paginatedTable from '@/mixins/paginatedTable';

export default {
    components: { PageHeader, TablePagination },
    mixins: [paginatedTable],
    data() {
        return {
            pendingLoaded: false,
            sentLoaded: false,

            pendingPagination: false,
            sentPagination: false,

            filterQueries: {
                pending: false,
                sent: false,
            },
            sortingOptions: {
                pending: false,
                sent: false,
            },
            searchFields: ['title', 'body', 'language', 'triggered_at', 'triggered_by'],
            organizations: false,
            organizationsLoading: false,

            sent: {
                notifications: [],
                paginationData: false,
                searchedNotifications: false,
                activeSorting: false,
                fields: [
                    {
                        value: 'title',
                        title: this.$t('notifications.title'),
                        sortable: true,
                        width: '15%',
                    },
                    {
                        value: 'body',
                        title: this.$t('notifications.body'),
                        sortable: true,
                        width: '40%',
                    },
                    {
                        value: 'locale',
                        title: this.$t('notifications.language'),
                        sortable: true,
                        width: '5%',
                    },
                    {
                        value: 'via_email',
                        title: this.$t('notifications.via_email'),
                        sortable: false,
                        width: '1%',
                    },
                    {
                        value: 'via_push',
                        title: this.$t('notifications.via_push'),
                        sortable: false,
                        width: '1%',
                    },
                    {
                        value: 'scheduled',
                        title: this.$t('notifications.sent'),
                        sortable: false,
                        width: '5%',
                    },
                    {
                        value: 'triggered_at',
                        title: this.$t('notifications.sent_on'),
                        sortable: true,
                        width: '5%',
                    },
                    {
                        value: 'triggered_by',
                        title: this.$t('notifications.triggered_by_name'),
                        sortable: true,
                        width: '5%',
                    },
                    {
                        value: 'organization_id',
                        title: this.$t('notifications.organization'),
                        sortable: false,
                        width: '5%',
                    },
                ],
            },

            pending: {
                notifications: [],
                paginationData: false,
                searchedNotifications: false,
                activeSorting: false,
                fields: [
                    {
                        value: 'title',
                        title: this.$t('notifications.title'),
                        sortable: true,
                        width: '15%',
                    },
                    {
                        value: 'body',
                        title: this.$t('notifications.body'),
                        sortable: true,
                        width: '40%',
                    },
                    {
                        value: 'locale',
                        title: this.$t('notifications.language'),
                        sortable: true,
                        width: '5%',
                    },
                    {
                        value: 'via_email',
                        title: this.$t('notifications.via_email'),
                        sortable: false,
                        width: '1%',
                    },
                    {
                        value: 'via_push',
                        title: this.$t('notifications.via_push'),
                        sortable: false,
                        width: '1%',
                    },
                    {
                        value: '',
                        title: '',
                        sortable: false,
                        width: '5%',
                    },
                    {
                        value: '',
                        title: '',
                        sortable: false,
                        width: '5%',
                    },
                ],
            },

            activeSearchTerm: '',
        };
    },
    created() {
        this.loadAllNotifications();
        if (!this.organizations) {
            this.loadOrganizations();
        }
    },
    computed: {
        filteredNotificationsPending() {
            const d = this.pending.searchedNotifications
                ? this.pending.searchedNotifications
                : this.pending.notifications;
            return d;
        },
        filteredNotificationsSent() {
            const d = this.sent.searchedNotifications
                ? this.sent.searchedNotifications
                : this.sent.notifications;
            return d;
        },
    },
    methods: {
        searchHandler: debounce(function(e) {
            this.activeSearchTerm = e;

            if (e !== '') {
                this.searchNotifications(e, 'pending');
                this.searchNotifications(e, 'sent');
            } else {
                this.pending.searchedNotifications = false;
                this.sent.searchedNotifications = false;

                this.loadAllNotifications();
            }
        }, 300),
        reloadNotifications() {
            if (this.activeSearchTerm) {
                this.searchNotifications(
                    this.activeSearchTerm,
                    'pending',
                    this.pending.paginationData.current_page
                );
                this.searchNotifications(
                    this.activeSearchTerm,
                    'sent',
                    this.sent.paginationData.current_page
                );
            } else {
                this.loadAllNotifications();
            }
        },
        loadAllNotifications() {
            this.loadPendingNotifications();
            this.loadSentNotifications();
        },

        loadPendingNotifications(page = 1) {
            let params = { page: page };

            if (Object.keys(this.pending.activeSorting).length > 0) {
                const field = Object.keys(this.pending.activeSorting)[0];
                params.order = `${field}${
                    this.pending.activeSorting[field] === 'asc' ? ' asc' : ' desc'
                }`;
            }

            api.notifications.getPending(params).then(
                response => {
                    this.pending.notifications = response.data.scheduled_notifications;
                    this.pending.paginationData = response.data._metadata;
                    this.pendingLoaded = true;
                },
                error => {
                    console.error(error);
                    // TODO: show error message
                }
            );
        },
        loadSentNotifications(page = 1) {
            let params = { page: page };

            if (Object.keys(this.sent.activeSorting).length > 0) {
                const field = Object.keys(this.sent.activeSorting)[0];
                params.order = `${field}${
                    this.sent.activeSorting[field] === 'asc' ? ' asc' : ' desc'
                }`;
            } else {
                params.order = 'triggered_at desc';
            }

            api.notifications.getSent(params).then(
                response => {
                    this.sent.notifications = response.data.scheduled_notifications;
                    this.sent.paginationData = response.data._metadata;
                    this.sentLoaded = true;
                },
                error => {
                    console.error(error);
                    // TODO: show error message
                }
            );
        },

        createEditNotification(isNew = false, notification = false) {
            this.$modal.show(
                () => import('@/views/modal/CreateEditNotification.vue'),
                {
                    isNew: isNew,
                    notification: notification,
                    organizations: this.organizations,
                    organizationsLoading: this.organizationsLoading,
                },
                {
                    name: 'CreateEditNotification',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': event => {
                        this.reloadNotifications();
                    },
                }
            );
        },
        viewSendNotification(notification, alreadySent = false) {
            this.$modal.show(
                () => import('@/views/modal/ViewSendNotification.vue'),
                {
                    notification: notification,
                    alreadySent: alreadySent,
                    organizations: this.organizations,
                },
                {
                    name: 'ViewSendNotification',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': event => {
                        this.reloadNotifications();
                    },
                }
            );
        },
        deleteNotification(notification) {
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: this.$t('notifications.delete_notification'),
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: () => {
                        notification.loading = true;

                        api.notifications.delete({ noteid: notification.id }).then(response => {
                            notification.loading = false;
                            this.reloadNotifications();
                        });
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        getOrganizationName(organizationId) {
            if (this.organizations && organizationId) {
                const organization = this.organizations.find(
                    organization => organization.id === organizationId
                );
                return organization ? organization.name : '';
            } else {
                return '';
            }
        },

        sortClickHandler(type, field) {
            if (this[type].activeSorting[field] === 'asc') {
                this[type].activeSorting = {};
                this[type].activeSorting[field] = 'desc';
            } else {
                this[type].activeSorting = {};
                this[type].activeSorting[field] = 'asc';
            }
            if (this.activeSearchTerm) {
                this.searchNotifications(
                    this.activeSearchTerm,
                    type,
                    this[type].paginationData.current_page
                );
            } else {
                if (type === 'pending') {
                    this.loadPendingNotifications(this[type].paginationData.current_page);
                } else {
                    this.loadSentNotifications(this[type].paginationData.current_page);
                }
            }
        },

        async searchNotifications(searchTerm, notificationType = 'pending', page = 1) {
            const allowedParams = this.searchFields;
            const searchParams = createSearchParams(searchTerm, allowedParams, 'title');

            if (Object.keys(searchParams).length <= 0) {
                this[notificationType].searchedNotifications = false;
                this[notificationType].notifications = false;
                return;
            }
            try {
                const params = {
                    ...searchParams,
                    page: page,
                };

                const original_triggered_at = params.hasOwnProperty('triggered_at')
                    ? params.triggered_at
                    : false;

                if (notificationType === 'pending') {
                    params.triggered_at = 'null';
                } else if (!params.hasOwnProperty('triggered_at')) {
                    params.triggered_at = 'any';

                    if (!params.hasOwnProperty('order')) {
                        params.order = 'triggered_at desc';
                    }
                }

                const response = await api.notifications.search({ params });
                if (response && response.data) {
                    if (notificationType === 'pending') {
                        if (original_triggered_at !== false) {
                            this.pending.searchedNotifications = [];
                            this.pending.paginationData = {
                                current_page: 1,
                                per_page: 10,
                                total_count: 0,
                                total_pages: 0,
                            };
                        } else {
                            this.pending.searchedNotifications =
                                response.data.scheduled_notifications;
                            this.pending.paginationData = response.data._metadata;
                        }
                    } else {
                        this.sent.searchedNotifications = response.data.scheduled_notifications;
                        this.sent.paginationData = response.data._metadata;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        },

        async loadOrganizations() {
            this.organizationsLoading = true;
            try {
                const response = await api.organizations.minimal();
                if (response && response.data) {
                    this.organizations = response.data.organizations;
                    this.organizationsLoading = false;
                    eventBus.$emit('allOrganizationsLoaded', {
                        organizations: this.organizations,
                        loading: this.organizationsLoading,
                    });
                }
            } catch (error) {
                console.error(error);
            }
        },

        pageHandler(notificationType = 'pending', direction = 1) {
            if (notificationType === 'pending') {
                const activePage = this.pending.paginationData
                    ? this.pending.paginationData.current_page
                    : 0;
                if (this.activeSearchTerm) {
                    this.searchNotifications(
                        this.activeSearchTerm,
                        notificationType,
                        activePage + direction
                    );
                } else {
                    this.loadPendingNotifications(activePage + direction);
                }
            } else {
                const activePage = this.sent.paginationData
                    ? this.sent.paginationData.current_page
                    : 0;
                if (this.activeSearchTerm) {
                    this.searchNotifications(
                        this.activeSearchTerm,
                        notificationType,
                        activePage + direction
                    );
                } else {
                    this.loadSentNotifications(activePage + direction);
                }
            }
        },
    },
    filters: {
        truncate: function(text, length, suffix) {
            if (text.length > length) {
                return text.substring(0, length) + suffix;
            } else {
                return text;
            }
        },
        formatDate: function(text) {
            return moment(text).format('YYYY-MM-DD HH:mm:ss');
        },
    },
};
</script>
